/**
 * String ⇄ API value conversions for `FormSheet` values. Blank means null so
 * a cleared field clears the column; money goes through the string-based
 * cents parser; bad input throws a message naming the field.
 */
import { centsToInput, parseDollarsToCents } from './format.js';

/** Trimmed text, or null when blank. */
export function text(v: string | undefined): string | null {
  const t = (v ?? '').trim();
  return t === '' ? null : t;
}

/** Dollars typed by a person → integer cents (null when blank). */
export function cents(v: string | undefined, label: string): number | null {
  if (!text(v)) return null;
  const c = parseDollarsToCents(v ?? '');
  if (c === null) throw new Error(`${label}: enter an amount like 12.50`);
  return c;
}

/** A whole number (null when blank). */
export function int(v: string | undefined, label: string): number | null {
  if (!text(v)) return null;
  const n = Number(v);
  if (!Number.isInteger(n)) throw new Error(`${label}: enter a whole number`);
  return n;
}

/** '30, 7, 0' → [30, 7, 0]. */
export function intList(v: string | undefined, label: string): number[] | null {
  if (!text(v)) return null;
  const parts = (v ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const out = parts.map(Number);
  if (out.some((n) => !Number.isInteger(n) || n < 0)) throw new Error(`${label}: whole days, comma separated`);
  return out;
}

/** 'a, b ,c' → ['a', 'b', 'c'] (lowercased, no empties, no duplicates). */
export function tagList(v: string | undefined): string[] {
  return [...new Set((v ?? '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean))];
}

/** Values for a form: null/undefined → '', numbers → strings, money cents → '12.50'. */
export function toValues(row: Record<string, unknown>, moneyKeys: readonly string[] = []): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(row)) {
    if (moneyKeys.includes(k)) out[k] = typeof v === 'number' ? centsToInput(v) : '';
    else if (v === null || v === undefined) out[k] = '';
    else if (Array.isArray(v)) out[k] = v.join(', ');
    else out[k] = String(v);
  }
  return out;
}

/** Select options from a string list, labelled by `label`. */
export function options(values: readonly string[], label: (v: string) => string = (v) => v): { value: string; label: string }[] {
  return values.map((v) => ({ value: v, label: label(v) }));
}
